export const queryKeys = {
  admin: {
    dashboard: () => ['admin', 'dashboard'] as const,
    users: (filters?: { page: number; limit: number }) => ['admin', 'users', filters] as const,
    studentSoa: (studentFeeStatusId: string) =>
      ['admin', 'student-soa', studentFeeStatusId] as const,
    cashDeposit: () => ['admin', 'cash-deposit'] as const,
    absentTeachers: () => ['admin', 'absent-teachers'] as const,
    feeStructures: () => ['admin', 'fee-structures'] as const,

    teacherAttendance: {
      search: () => ['admin', 'teacher-attendance', 'search'] as const,
      searchByDate: (date: string) =>
        ['admin', 'teacher-attendance', 'search-by-date', date] as const,
      fetchTeachersForAttendance: () =>
        ['admin', 'teacher-attendance', 'fetch-teachers-for-attendance'] as const,
      attendanceSheet: () => ['admin', 'teacher-attendance', 'attendance-sheet'] as const,
    },

    academics: {
      standardDivisions: (schoolYearId?: string) =>
        ['admin', 'academics', 'standard-divisions', schoolYearId ?? 'current'] as const,
      timeTable: (schoolYearId?: string, stdId?: number) =>
        ['admin', 'academics', 'time-table', schoolYearId ?? 'current', stdId ?? 'all'] as const,
      stdPromoted: () => ['admin', 'academics', 'std-promoted'] as const,
      teachersDropdown: () => ['admin', 'academics', 'teachers-dropdown'] as const,
      classTeacher: (standard: string, div: string) =>
        ['admin', 'academics', 'class-teacher', standard, div] as const,
      previousExams: () => ['admin', 'academics', 'previous-exams'] as const,
      weeklyPlanner: () => ['admin', 'academics', 'weekly-planner'] as const,
      homeworkPlanner: () => ['admin', 'academics', 'homework-planner'] as const,
      syllabus: () => ['admin', 'academics', 'syllabus'] as const,
      manageSubjects: () => ['admin', 'academics', 'manage-subjects'] as const,
      manageTeachers: () => ['admin', 'academics', 'manage-teachers'] as const,
      schoolSettings: {
        schoolSettings: (schoolYearId?: string) =>
          ['admin', 'school-settings', schoolYearId ?? 'current'] as const,
        schoolYear: () => ['admin', 'school-year'] as const,
        terms: () => ['admin', 'terms'] as const,
        assessments: () => ['admin', 'assessments'] as const,
      },
    },
    fees: {
      rateCategories: () => ['fees', 'rate-categories'] as const,
      rateStructure: () => ['fees', 'rate-structure'] as const,
      standards: () => ['fees', 'standards'] as const,
      pendingFees: (standard: string, schoolYearId: string) =>
        ['fees', 'pending-fees', standard, schoolYearId] as const,
      halfPaidFees: (standard: string, schoolYearId: string) =>
        ['fees', 'half-paid-fees', standard, schoolYearId] as const,

      // fee structure
      schoolFees: () => ['fees', 'school-fees'] as const,
      categoryRates: () => ['fees', 'category-rates'] as const,
    },
    disciplineRecords: {
      list: () => ['admin', 'discipline-records', 'list'] as const,
      students: () => ['admin', 'discipline-records', 'students'] as const,
    },
    leaves: {
      teacher: () => ['admin', 'leaves', 'teacher'] as const,
      student: () => ['admin', 'leaves', 'student'] as const,
      staff: () => ['admin', 'leaves', 'staff'] as const,
    },
    subjects: {
      catalog: () => ['admin', 'subjects', 'catalog'] as const,
    },
    library: {
      searchBook: (isbn: string) => ['admin', 'library', 'search-book', isbn] as const,
      searchLibrary: (searchText: string, page: number) =>
        ['admin', 'library', 'search-library', searchText, page] as const,
      // Infinite version (no page in key)
      searchLibraryInfinite: (searchText: string) =>
        ['admin', 'library', 'search-library', 'infinite', searchText] as const,
      isbnSearch: (isbn: string) => ['admin', 'library', 'isbn-search', isbn] as const,
      addBook: () => ['admin', 'library', 'add-book'] as const,
      returnBook: () => ['admin', 'library', 'return-book'] as const,
      issueBook: () => ['admin', 'library', 'issue-book'] as const,
      borrowers: (bookId: string) => ['admin', 'library', 'borrowers', bookId] as const,
    },
    inventory: {
      list: () => ['admin', 'inventory', 'list'] as const,
      item: (id: number) => ['admin', 'inventory', 'item', id] as const,
      borrowers: (id: number) => ['admin', 'inventory', 'borrowers', id] as const,
      addItem: () => ['admin', 'inventory', 'add-item'] as const,
      issueItem: () => ['admin', 'inventory', 'issue-item'] as const,
      returnItem: () => ['admin', 'inventory', 'return-item'] as const,
    },
    grading: {
      standardsAndSubjects: () => ['admin', 'grading', 'standards-and-subjects'] as const,
    },
    reports: {
      schoolYears: () => ['admin', 'reports', 'schoolYears'] as const,
      studentList: (
        page: number,
        pageSize: number,
        filters: {
          search: string;
          rte?: boolean;
          graduated?: boolean;
          standard: string;
          div: string;
          schoolYearId: string;
          category: string;
          caste: string;
          religion: string;
          sex: string;
          feeStatus: string;
          feeMethod: string;
          busNo: string;
          busStatus: string;
          is_active?: boolean;
        }
      ) =>
        [
          'admin',
          'reports',
          'student-list',
          page,
          pageSize,
          filters.search,
          filters.rte,
          filters.graduated,
          filters.standard,
          filters.div,
          filters.schoolYearId,
          filters.category,
          filters.caste,
          filters.religion,
          filters.sex,
          filters.feeStatus,
          filters.feeMethod,
          filters.busNo,
          filters.busStatus,
          filters.is_active,
        ] as const,
      dailyFinancialReport: (date: string) =>
        ['admin', 'reports', 'daily-financial-report', date] as const,
      classWiseGrades: (params: { schoolYearId: string; standard: string; division: string }) =>
        [
          'admin',
          'reports',
          'classWiseGrades',
          params.schoolYearId,
          params.standard,
          params.division,
        ] as const,
      gradesBySchoolYear: (schoolYearIds: string[]) =>
        ['admin', 'reports', 'gradesBySchoolYear', ...schoolYearIds] as const,
      classWiseFeeReport: (schoolYearId: string, standards: string[], divisions: string[]) =>
        ['admin', 'reports', 'class-wise-fee-report', schoolYearId, ...standards, ...divisions] as const,
      busFeeReport: (
        schoolYearId: string,
        standards: string[],
        divisions: string[],
        busNos: string[]
      ) =>
        ['admin', 'reports', 'bus-fee-report', schoolYearId, ...standards, ...divisions, ...busNos] as const,
      grReport: () => ['admin', 'reports', 'gr-report'] as const,
      duplicateStudents: (filters: { includeInactive: boolean; includeGraduated: boolean }) =>
        [
          'admin',
          'reports',
          'duplicate-students',
          filters.includeInactive,
          filters.includeGraduated,
        ] as const,
      documentChecklistReport: (
        page: number,
        pageSize: number,
        search: string,
        filters: {
          aadhar: string;
          birth_certificate: string;
          lc: string;
          bonafied: string;
          mother_aadhar: string;
          father_aadhar: string;
          address_proof: string;
          student_photo: string;
          mother_photo: string;
          father_photo: string;
          previous_school_report_card: string;
        }
      ) =>
        [
          'admin',
          'reports',
          'document-checklist-report',
          page,
          pageSize,
          search,
          filters.aadhar,
          filters.birth_certificate,
          filters.lc,
          filters.bonafied,
          filters.mother_aadhar,
          filters.father_aadhar,
          filters.address_proof,
          filters.student_photo,
          filters.mother_photo,
          filters.father_photo,
          filters.previous_school_report_card,
        ] as const,
    },
  },
  teacher: {
    weeklyPlanner: () => ['teacher', 'weekly-planner'] as const,
    dashboard: {
      classTeacherOf: () => ['teacher', 'dashboard', 'classTeacherOf'] as const,
    },
    attendance: {
      classStudents: () => ['teacher', 'attendance', 'class-students'] as const,
      searchAttendanceReport: (date: string) =>
        ['teacher', 'attendance', 'search-attendance-report', date] as const,
      attendanceSheet: () => ['teacher', 'attendance', 'attendance-sheet'] as const,
    },
    subjectHome: {
      fetchSubjectHome: (subjectId: string) => ['teacher', 'subject-home', subjectId] as const,
      fetchUpcoming: (subjectId: string) =>
        ['teacher', 'subject-home', subjectId, 'upcoming'] as const,
      checkWeightage: (subjectId: string) =>
        ['teacher', 'subject-home', subjectId, 'check-weightage'] as const,
    },
    subject: {
      studentsInSubject: (subjectId: string) =>
        ['teacher', 'subject', 'students-in-subject', subjectId] as const,
      students: {
        telephoneRecords: (studentId: string, startDate?: string, endDate?: string) =>
          [
            'teacher',
            'subject',
            'students',
            'telephone-records',
            studentId,
            startDate,
            endDate,
          ] as const,
        disciplineRecords: (subjectId: string, studentId: string) =>
          ['teacher', 'subject', subjectId, 'students', 'discipline-records', studentId] as const,
      },
      weightage: {
        terms: () => ['teacher', 'subject', 'weightage', 'terms'] as const,
        assessments: () => ['teacher', 'subject', 'weightage', 'assessments'] as const,
        subjectTermWeightage: (subjectId: string, termId: string) =>
          ['teacher', 'subject', 'weightage', subjectId, termId] as const,
      },
      syllabus: {
        files: (subjectId: string) => ['teacher', 'subject', 'syllabus', subjectId] as const,
      },
      modules: {
        all: (subjectId: string) => ['teacher', 'subject', 'modules', subjectId] as const,
      },
      assessments: {
        list: (subjectId: string) => ['teacher', 'subject', 'assessments', subjectId] as const,
        subjectAssessments: (subjectId: string) =>
          ['teacher', 'subject', 'assessments', 'subject-assessments', subjectId] as const,
        weightage: (subjectId: string) =>
          ['teacher', 'subject', 'assessments', 'weightage', subjectId] as const,
        open: (id: string) => ['teacher', 'subject', 'assessments', 'open', id] as const,
        grade: (id: string) => ['teacher', 'subject', 'assessments', 'grade', id] as const,
        stats: (id: string) => ['teacher', 'subject', 'assessments', 'stats', id] as const,
        speedGrade: (id: string) =>
          ['teacher', 'subject', 'assessments', 'speed-grade', id] as const,
      },
      grading: {
        gradeBook: (subjectId: string) =>
          ['teacher', 'subject', 'grading', 'grade-book', subjectId] as const,
        gradeBookWeightage: (subjectId: string) =>
          ['teacher', 'subject', 'grading', 'grade-book-weightage', subjectId] as const,
        finalGradeStatus: (subjectId: string) =>
          ['teacher', 'subject', 'grading', 'final-grade-status', subjectId] as const,
        schoolYear: () => ['teacher', 'subject', 'grading', 'school-year'] as const,
        markTypes: (subjectId: string) =>
          ['teacher', 'subject', 'grading', 'mark-types', subjectId] as const,
        markTypeDetails: (markTypeId: string) =>
          ['teacher', 'subject', 'grading', 'mark-type-details', markTypeId] as const,
      },
    },
    myStudents: () => ['teacher', 'my-students'] as const,
    leaves: () => ['teacher', 'leaves'] as const,
    subjects: () => ['teacher', 'subjects'] as const,
    students: (subjectId: string) => ['teacher', 'subjects', subjectId, 'students'] as const,
  },
  student: {
    profile: () => ['student', 'profile'] as const,
    assignments: () => ['student', 'assignments'] as const,
    grades: {
      checkFeeStatus: () => ['student', 'grades', 'check-fee-status'] as const,
      listFinalClassGrades: () => ['student', 'grades', 'list-final-class-grades'] as const,
      schoolSettingsExist: () => ['student', 'grades', 'school-settings-exist'] as const,
      viewFinalClassGrades: (schoolYearId: string) =>
        ['student', 'grades', 'view-final-class-grades', schoolYearId] as const,
    },
    viewAttendance: () => ['student', 'view-attendance'] as const,
    subject: {
      home: (subjectId: string) => ['student', 'subject', 'home', subjectId] as const,
      syllabus: (subjectId: string) => ['student', 'subject', 'syllabus', subjectId] as const,

      modules: (subjectId: string) => ['student', 'subject', 'modules', subjectId] as const,
      assessments: {
        list: (subjectId: string) => ['student', 'subject', 'assessments', subjectId] as const,
        fetchAssessments: () => ['student', 'subject', 'assessments', 'fetch-assessments'] as const,
        openAssessment: (assessmentId: string) =>
          ['student', 'subject', 'assessments', 'open-assessment', assessmentId] as const,
      },
      grades: {
        all: (subjectId: string) => ['student', 'subject', 'grades', subjectId] as const,
        weightage: (subjectId: string) =>
          ['student', 'subject', 'grades', 'weightage', subjectId] as const,
      },
    },
    fees: {
      paidFees: () => ['student', 'fees', 'paid-fees'] as const,
      paidFeesDetails: (soaId: string) =>
        ['student', 'fees', 'paid-fees', 'details', soaId] as const,
    },
  },
  profile: {
    user: (username: string) => ['profile', 'user', username] as const,
    admin: (username: string) => ['profile', 'admin', username] as const,
    teacher: (username: string) => ['profile', 'teacher', username] as const,
    teacherSubjectActivity: (username: string) =>
      ['profile', 'teacher-subject-activity', username] as const,
    subscriptionHistory: (studentId: string) =>
      ['profile', 'subscription-history', studentId] as const,
    studentReport: (studentId: string) => ['profile', 'student-report', studentId] as const,
    feeHistory: (username: string) => ['profile', 'fee-history', username] as const,
    adminPermissions: (username: string) => ['profile', 'admin-permissions', username] as const,
    documentChecklist: (username: string) => ['profile', 'document-checklist', username] as const,
    tcLc: (studentId: string) => ['profile', 'tc-lc', studentId] as const,
    bonafied: (studentId: string) => ['bonafied', studentId] as const,
  },
  announcements: {
    list: (page: number) => ['announcements', page] as const,
    subject: (subjectId: string, page: number) =>
      ['announcements', 'subject', subjectId, page] as const,
  },
  common: {
    fetchSubjects: () => ['common', 'fetch-subjects'] as const,
    schoolCalendar: () => ['common', 'school-calendar'] as const,
    schoolCalendarBulkStatus: () => ['common', 'school-calendar-bulk-status'] as const,
    searchUsers: (username: string, page: number, pageSize: number) =>
      ['common', 'search-users', username, page, pageSize] as const,
    searchUsersInfinite: (username: string, pageSize: number) =>
      ['common', 'search-users', 'infinite', username, pageSize] as const,
  },
} as const;
